import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Chip,
} from '@mui/material';
import type { Bot, BotTrigger } from '../../types';
import { createBot, updateBot } from '../../services/bots';

const TRIGGER_OPTIONS: { value: BotTrigger; label: string }[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'on_item_add', label: 'On Item Add' },
  { value: 'schedule', label: 'Schedule' },
];

const SCHEDULE_PRESETS = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays (Mon–Fri)' },
];

const DAY_OPTIONS = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

interface BotDialogProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  bot?: Bot | null;
}

export default function BotDialog({ open, onClose, onSaved, bot }: BotDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [seq, setSeq] = useState(0);
  const [triggerType, setTriggerType] = useState<BotTrigger>('manual');
  const [schedulePreset, setSchedulePreset] = useState('daily');
  const [customDays, setCustomDays] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (bot) {
      setName(bot.name);
      setDescription(bot.description);
      setInstructions(bot.instructions);
      setSeq(bot.seq);
      setTriggerType(bot.triggerType);
      if (bot.schedule) {
        if (['hourly', 'daily', 'weekdays'].includes(bot.schedule)) {
          setSchedulePreset(bot.schedule);
          setCustomDays([]);
        } else {
          setSchedulePreset('custom');
          setCustomDays(bot.schedule.split(','));
        }
      }
    } else {
      setName('');
      setDescription('');
      setInstructions('');
      setSeq(0);
      setTriggerType('manual');
      setSchedulePreset('daily');
      setCustomDays([]);
    }
  }, [bot, open]);

  const getScheduleValue = (): string | undefined => {
    if (triggerType !== 'schedule') return undefined;
    if (schedulePreset === 'custom') return customDays.join(',');
    return schedulePreset;
  };

  const handleSave = async () => {
    if (!name.trim() || !instructions.trim()) return;
    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        description: description.trim(),
        instructions: instructions.trim(),
        seq,
        triggerType,
        schedule: getScheduleValue(),
      };
      if (bot) {
        await updateBot(bot.id, data);
      } else {
        await createBot(data);
      }
      onClose();
      onSaved();
    } catch (err) {
      console.error('Failed to save bot:', err);
      alert('Failed to save bot.');
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (day: string) => {
    setCustomDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{bot ? 'Edit Bot' : 'New Bot'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          fullWidth
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          size="small"
          placeholder="Short description of what this bot does (shown to AI)"
        />
        <TextField
          label="Sequence"
          type="number"
          value={seq}
          onChange={(e) => setSeq(parseInt(e.target.value) || 0)}
          fullWidth
          size="small"
        />
        <TextField
          label="Instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          required
          fullWidth
          multiline
          rows={6}
          placeholder="Tell the bot what to do with your saved items..."
        />
        <FormControl fullWidth size="small">
          <InputLabel>Trigger</InputLabel>
          <Select
            value={triggerType}
            label="Trigger"
            onChange={(e) => setTriggerType(e.target.value as BotTrigger)}
          >
            {TRIGGER_OPTIONS.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {triggerType === 'schedule' && (
          <>
            <FormControl fullWidth size="small">
              <InputLabel>Schedule</InputLabel>
              <Select
                value={schedulePreset}
                label="Schedule"
                onChange={(e) => setSchedulePreset(e.target.value)}
              >
                {SCHEDULE_PRESETS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
                <MenuItem value="custom">Custom Days</MenuItem>
              </Select>
            </FormControl>

            {schedulePreset === 'custom' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {DAY_OPTIONS.map((d) => (
                  <Chip
                    key={d.value}
                    label={d.label}
                    onClick={() => toggleDay(d.value)}
                    color={customDays.includes(d.value) ? 'primary' : 'default'}
                    variant={customDays.includes(d.value) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !name.trim() || !instructions.trim()}
        >
          {bot ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
